import React from 'react';
import {Box, Card, InputLabel, MenuItem, Stack, TextField, Typography} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import WarningIcon from '@mui/icons-material/Warning';

import {TableDropdown} from '../../../components/molecules/TableDropdown';
import {EXCEPTION_CATEGORY_NAMES} from '../../../constants/contants';
import {cardHeadingSx, fieldLabelSx} from './headerAnalysisRulePresentation';

const LEVEL_LABELS: Record<string, string> = {
  clean: EXCEPTION_CATEGORY_NAMES.clean,
  undetermined: EXCEPTION_CATEGORY_NAMES.undetermined,
  threat: EXCEPTION_CATEGORY_NAMES.threat,
};

const LEVEL_ACTION_DEFAULTS: Record<string, {priority: string; category: string}> = {
  clean: {priority: 'Low', category: 'Clean'},
  undetermined: {priority: 'Medium', category: 'Threat'},
  threat: {priority: 'High', category: 'Threat'},
};

const LEVEL_SCORE_DEFAULTS: Record<string, number> = {
  clean: 0,
  undetermined: 50,
  threat: 70,
};

const threatScoringHeadingSx = {
  color: 'inherit',
  fontFamily: 'Montserrat',
  fontSize: 15,
  fontStyle: 'normal',
  fontWeight: 700,
  letterSpacing: 0,
  lineHeight: 1,
};

const scoreRangeSx = {
  color: 'var(--text-primary-text-High, #000000E5)',
  fontFamily: 'Montserrat',
  fontSize: 12,
  fontStyle: 'normal',
  fontWeight: 400,
  letterSpacing: 0,
  lineHeight: 1.6,
  pt: 0.5,
};

const actionCategories = {Threat: 'Threat', Clean: 'Clean', Unknown: 'Unknown'};
const actionStatuses = {
  Received: {bg: '#EDEDED', hover: '#EDEDED'},
  'In Review': {bg: '#E1EFEF', hover: '#E1EFEF'},
  Resolved: {bg: '#F2FAD9', hover: '#F2FAD9'},
};
const actionPriorities = {
  High: {bg: '#ED1C24', hover: '#B20007'},
  Medium: {bg: '#FCAB10', hover: '#D88E00'},
  Low: {bg: '#03A9F5', hover: '#007AB2'},
  Unknown: {bg: '#909090', hover: '#707070'},
};

const normalizeScore = (rawValue: string): number => {
  const parsedValue = Number(rawValue);
  if (!Number.isFinite(parsedValue)) return 0;
  return Math.min(100, Math.max(0, parsedValue));
};

interface MatchingEmailActionsCardProps {
  level: string;
  threatScoreContribution: number;
  actionTaken: {
    priority: string;
    category: string;
    processStatus: string;
  };
  scoreError?: string;
  onChange: (patch: {
    level?: string;
    threatScoreContribution?: number;
    actionTaken?: MatchingEmailActionsCardProps['actionTaken'];
  }) => void;
  onActionChange: (key: string, value: string) => void;
}

export const MatchingEmailActionsCard: React.FC<MatchingEmailActionsCardProps> = ({
  level,
  threatScoreContribution,
  actionTaken,
  scoreError,
  onChange,
  onActionChange,
}) => (
  <Card data-testid="actions-card" variant="outlined" sx={{p: 3, height: '100%', boxSizing: 'border-box'}}>
    <Typography className="rule-card-heading" sx={{...cardHeadingSx, mb: 3}}>Actions for matching emails</Typography>
    <Box className={`authentication-checks-threat-score ${level}`} sx={{p: 3, borderRadius: 2, mb: 3}}>
      <Stack className="threat-score-stack" direction="row" alignItems="center" spacing={1} pb={2} mb={2}>
        <SettingsIcon/>
        <Typography className="threat-scoring-heading" sx={threatScoringHeadingSx}>
          Threat Level & Scoring
        </Typography>
      </Stack>
      <Stack spacing={2.5}>
        <Box>
          <InputLabel id="header-analysis-threat-level-label" htmlFor="header-analysis-threat-level" sx={{...fieldLabelSx, color: 'inherit'}}>Email Threat Level</InputLabel>
          <TextField
            id="header-analysis-threat-level"
            select
            size="small"
            value={level}
            onChange={event => {
              const nextLevel = event.target.value;
              onChange({
                level: nextLevel,
                threatScoreContribution: LEVEL_SCORE_DEFAULTS[nextLevel],
                actionTaken: {...actionTaken, ...LEVEL_ACTION_DEFAULTS[nextLevel]},
              });
            }}
            SelectProps={{
              labelId: 'header-analysis-threat-level-label',
              renderValue: selected => (
                <Stack direction="row" alignItems="center" spacing={1} sx={{color: 'text.primary'}}>
                  {selected === 'clean' && <CheckCircleIcon className="header-analysis-no-threat-icon" htmlColor="#03A9F5" fontSize="small"/>}
                  {selected === 'undetermined' && <RemoveCircleIcon color="warning" fontSize="small"/>}
                  {selected === 'threat' && <WarningIcon color="error" fontSize="small"/>}
                  <Typography fontWeight={700}>{LEVEL_LABELS[String(selected)]}</Typography>
                </Stack>
              ),
            }}
            sx={{width: 200, maxWidth: '100%', bgcolor: 'background.paper'}}
          >
            <MenuItem value="clean"><CheckCircleIcon className="header-analysis-no-threat-icon" sx={{mr: 1}} htmlColor="#03A9F5" fontSize="small"/>{LEVEL_LABELS.clean}</MenuItem>
            <MenuItem value="undetermined"><RemoveCircleIcon color="warning" sx={{mr: 1}} fontSize="small"/>{LEVEL_LABELS.undetermined}</MenuItem>
            <MenuItem value="threat"><WarningIcon color="error" sx={{mr: 1}} fontSize="small"/>{LEVEL_LABELS.threat}</MenuItem>
          </TextField>
        </Box>
        <Box>
          <InputLabel htmlFor="header-analysis-threat-score" sx={{...fieldLabelSx, color: 'inherit'}}>Email Threat Score</InputLabel>
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <TextField
              id="header-analysis-threat-score"
              size="small"
              type="number"
              value={threatScoreContribution}
              onChange={event => {
                const normalizedScore = normalizeScore(event.target.value);
                event.target.value = String(normalizedScore);
                onChange({threatScoreContribution: normalizedScore});
              }}
              onKeyDown={event => {
                if (event.key === '-') event.preventDefault();
              }}
              inputProps={{min: 0, max: 100, step: 1}}
              error={Boolean(scoreError)} helperText={scoreError}
              sx={{width: 100, bgcolor: 'background.paper'}}
            />
            <Typography className="score-range-text" sx={scoreRangeSx}>
              Score range:<br/><strong>0-100</strong>
            </Typography>
          </Stack>
        </Box>
      </Stack>
    </Box>
    <Box data-testid="matching-email-actions" sx={{bgcolor: 'grey.100', borderRadius: 1, px: 3, py: 1}}>
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2, borderBottom: 1, borderColor: 'divider', gap: 2}}>
        <Typography>Priority:</Typography>
        <TableDropdown
          title={actionTaken.priority}
          variant="priority"
          color="#FFFFFF"
          background={actionPriorities[actionTaken.priority as keyof typeof actionPriorities]?.bg}
          backgroundHover={actionPriorities[actionTaken.priority as keyof typeof actionPriorities]?.hover}
          borderRadius={100}
          data={actionPriorities}
          onValueChange={value => onActionChange('priority', value)}
          ariaLabel="Priority"
          fontSize={14}
          justifyContent="center"
        />
      </Box>
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2, borderBottom: 1, borderColor: 'divider', gap: 2}}>
        <Typography>Status:</Typography>
        <TableDropdown
          title={actionTaken.processStatus}
          variant="status"
          color="#212121"
          background={actionStatuses[actionTaken.processStatus as keyof typeof actionStatuses]?.bg}
          backgroundHover={actionStatuses[actionTaken.processStatus as keyof typeof actionStatuses]?.hover}
          borderRadius={2}
          padding="4px 4px 4px 8px"
          data={actionStatuses}
          onValueChange={value => onActionChange('processStatus', value)}
          ariaLabel="Status"
          fontSize={14}
        />
      </Box>
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2, gap: 2}}>
        <Typography>Category:</Typography>
        <TableDropdown
          title={actionTaken.category}
          variant="category"
          color="#212121"
          background="transparent"
          backgroundHover="transparent"
          borderRadius={100}
          data={actionCategories}
          onValueChange={value => onActionChange('category', value)}
          ariaLabel="Category"
          fontSize={14}
          width="auto"
        />
      </Box>
    </Box>
  </Card>
);
